import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  AlertTriangle,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Droplet,
  Flame,
  HelpCircle,
  LayoutList,
  Loader2,
  RefreshCw,
  RotateCcw,
  Save,
  Wifi,
  Zap,
} from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import MonthlyServicesGrid, { cellKey } from './components/MonthlyServicesGrid';
import {
  buildCreateDateIso,
  buildMonthDays,
  buildMonthRange,
  buildMonthStartIso,
  buildUpdateDateIso,
  computeConsumo,
  computeCosto,
  computeLitros,
  formatCurrency,
  formatNumber,
  getRecordDayKey,
  isSameNumber,
  MONTH_NAMES,
  parseReading,
  resolveUnitPrice,
} from './utils/utilityServicesGrid.utils';
import {
  createUtilityService,
  deleteUtilityService,
  fetchServicesByLaundry,
  fetchUtilityServices,
  updateServiceCounter,
  updateUtilityService,
} from './services/utilityServices.services';

/** Tope holgado: 31 días x servicios de una lavandería caben de sobra. */
const MONTH_FETCH_LIMIT = 500;
/** Registros previos que se leen para resolver la lectura base por servicio. */
const BASELINE_FETCH_LIMIT = 80;

const SERVICE_ICONS = {
  'Agua': Droplet,
  'Agua caliente': Droplet,
  'Gas': Flame,
  'Electricidad': Zap,
  'Internet': Wifi,
};

const SERVICE_COLORS = {
  'Agua': '#3b82f6',
  'Agua caliente': '#f34d24',
  'Gas': '#d97706',
  'Electricidad': '#eab308',
  'Internet': '#8b5cf6',
};

/**
 * Resuelve la lectura base de cada servicio, en este orden:
 *
 *  1. El último registro anterior al mes. Es la única base plenamente
 *     confiable, así que queda bloqueada.
 *  2. Si no hay registro anterior pero el mes ya tiene lecturas, se toma el
 *     `contadorAnterior` que guardó la primera de ellas: el propio registro
 *     ya sabe de dónde venía, así que la cadena arranca donde dicen los datos
 *     y el mes no aparenta estar mal.
 *  3. Sólo si el mes está vacío para ese servicio se propone el contador de la
 *     parametrización, editable. Ese campo lo reescribe el backend con cada
 *     lectura guardada, así que siempre refleja la última lectura y nunca
 *     sirve de base para un mes que ya tiene registros.
 *  4. Sin nada de lo anterior queda vacía, para que el operario la escriba.
 */
const resolveBaselines = (serviceList, monthRecords, priorRecords) => {
  // El backend ordena por fecha descendente: la primera aparición de cada
  // servicio es su lectura más reciente antes del mes en curso.
  const latestPrior = {};
  priorRecords.forEach((record) => {
    const serviceId = record?.servicio?.id;
    if (serviceId && !latestPrior[serviceId]) latestPrior[serviceId] = record;
  });

  // Primer registro del mes por servicio (el de fecha más temprana).
  const firstOfMonth = {};
  monthRecords.forEach((record) => {
    const serviceId = record?.servicio?.id;
    const dayKey = getRecordDayKey(record);
    if (!serviceId || !dayKey) return;
    const current = firstOfMonth[serviceId];
    if (!current || dayKey < current.dayKey) firstOfMonth[serviceId] = { record, dayKey };
  });

  const baselines = {};
  serviceList.forEach((service) => {
    const prior = latestPrior[service._id];
    const first = firstOfMonth[service._id];

    if (prior && Number.isFinite(Number(prior.contadorNuevo))) {
      baselines[service._id] = {
        value: Number(prior.contadorNuevo),
        source: 'record',
        label: prior.fecha ? format(new Date(prior.fecha), 'dd/MM/yy', { locale: es }) : 'anterior',
      };
    } else if (first && Number.isFinite(Number(first.record.contadorAnterior))) {
      baselines[service._id] = {
        value: Number(first.record.contadorAnterior),
        source: 'record',
        label: `previo al ${Number(first.dayKey.slice(-2))}`,
      };
    } else if (!first && Number.isFinite(Number(service.contador))) {
      baselines[service._id] = {
        value: String(service.contador),
        source: 'param',
        label: 'base de parametrización',
      };
    } else {
      baselines[service._id] = { value: '', source: 'none', label: 'sin registro previo' };
    }
  });

  return baselines;
};

/**
 * Registro de servicios públicos — vista de planilla mensual (V2).
 *
 * Alternativa a `UtilityServicesPage`: en lugar de una tarjeta por servicio
 * más una tabla paginada con un modal por registro, presenta el mes completo
 * como una planilla editable (un renglón por día, una columna por medidor) y
 * guarda todos los cambios en un solo lote.
 *
 * No modifica ni reutiliza el estado de la vista clásica: consume los mismos
 * endpoints y convive con ella sin interferencias.
 */
const UtilityServicesGridPage = () => {
  const { idLavanderia } = useParams();
  const { toast } = useToast();
  const { isAdmin } = useAuth();

  const canEditExisting = isAdmin();

  // ---------- PERIODO ----------
  const now = new Date();
  const [period, setPeriod] = useState({ year: now.getFullYear(), month: now.getMonth() });

  // ---------- DATOS ----------
  const [services, setServices] = useState([]);
  const [records, setRecords] = useState([]);
  const [laundryName, setLaundryName] = useState('');
  const [baselines, setBaselines] = useState({});
  const [hiddenServices, setHiddenServices] = useState({});

  // ---------- EDICIÓN ----------
  const [drafts, setDrafts] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  // Descarta respuestas de cargas que quedaron obsoletas (ver loadAll).
  const loadTokenRef = useRef(0);

  // ---------- DIÁLOGOS ----------
  const [pendingDeletion, setPendingDeletion] = useState(null);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const [pendingSave, setPendingSave] = useState(null);
  const [pendingRecalc, setPendingRecalc] = useState(false);

  const monthDays = useMemo(
    () => buildMonthDays(period.year, period.month),
    [period.year, period.month]
  );

  const visibleServices = useMemo(
    () => services.filter((service) => !hiddenServices[service._id]),
    [services, hiddenServices]
  );

  // ---------- CARGA ----------

  /**
   * Trae de una sola vez todo lo que la planilla necesita: los servicios de la
   * lavandería, los registros del mes y los registros anteriores al mes (para
   * la lectura base). No toca el estado: sólo devuelve datos.
   */
  const fetchGridData = useCallback(async () => {
    if (!idLavanderia) return { serviceList: [], monthRecords: [], priorRecords: [] };

    const servicesResponse = await fetchServicesByLaundry(idLavanderia);
    const serviceList = Array.isArray(servicesResponse) ? servicesResponse : [];

    const [monthResponse, priorResponse] = await Promise.all([
      fetchUtilityServices({
        limit: MONTH_FETCH_LIMIT,
        page: 1,
        query: {
          'lavanderia.id': idLavanderia,
          fecha: buildMonthRange(period.year, period.month),
        },
      }),
      fetchUtilityServices({
        limit: BASELINE_FETCH_LIMIT,
        page: 1,
        query: {
          'lavanderia.id': idLavanderia,
          fecha: { $lt: buildMonthStartIso(period.year, period.month) },
        },
      }),
    ]);

    return {
      serviceList,
      monthRecords: Array.isArray(monthResponse?.data) ? monthResponse.data : [],
      priorRecords: Array.isArray(priorResponse?.data) ? priorResponse.data : [],
    };
  }, [idLavanderia, period.year, period.month]);

  /**
   * Carga la planilla y publica el resultado.
   *
   * Cada carga lleva un token: si el usuario cambia de mes mientras hay
   * peticiones en vuelo, la respuesta vieja se descarta. Sin esto una carga
   * tardía puede dejar la base de un mes junto a los registros de otro, y
   * entonces la planilla entera aparenta estar mal.
   *
   * `quiet` omite el indicador de carga, para refrescar después de guardar o
   * eliminar sin desmontar la cuadrícula que el operario está mirando.
   */
  const loadAll = useCallback(async ({ quiet = false } = {}) => {
    const token = loadTokenRef.current + 1;
    loadTokenRef.current = token;
    const isStale = () => token !== loadTokenRef.current;

    if (!quiet) setIsLoading(true);
    try {
      const { serviceList, monthRecords, priorRecords } = await fetchGridData();
      if (isStale()) return;

      setServices(serviceList);
      if (serviceList[0]?.lavanderia?.nombre) setLaundryName(serviceList[0].lavanderia.nombre);
      setRecords(monthRecords);
      setBaselines(resolveBaselines(serviceList, monthRecords, priorRecords));
    } catch (error) {
      if (isStale()) return;
      console.error('Error al cargar la planilla:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.message || 'No se pudo cargar la planilla mensual.',
      });
    } finally {
      if (!isStale() && !quiet) setIsLoading(false);
    }
  }, [fetchGridData, toast]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // ---------- MODELO DE LA PLANILLA ----------

  /**
   * Índice `servicio|día -> registro`. Ante duplicados se conserva el más
   * reciente (el backend entrega la lista ordenada por fecha descendente).
   */
  const recordIndex = useMemo(() => {
    const index = {};
    records.forEach((record) => {
      const dayKey = getRecordDayKey(record);
      const serviceId = record?.servicio?.id;
      if (!dayKey || !serviceId) return;
      const key = cellKey(serviceId, dayKey);
      if (!index[key]) index[key] = record;
    });
    return index;
  }, [records]);

  /**
   * Recorre cada servicio día por día encadenando las lecturas: cada valor
   * hereda como contador anterior la última lectura válida previa. Produce
   * las celdas para la planilla, los totales del mes y los planes de guardado
   * (ediciones directas, arrastre de la cadena y desfases históricos).
   */
  const model = useMemo(() => {
    const cells = {};
    const totals = {};
    const directEdits = [];
    const cascade = [];
    const drift = [];
    const invalid = [];
    const overLimit = [];

    services.forEach((service) => {
      const baseline = baselines[service._id];
      const baselineValue = parseReading(baseline?.value);
      let previous = Number.isFinite(baselineValue) ? baselineValue : null;

      let firstEditedIndex = Infinity;
      const serviceRows = [];
      let totalConsumo = 0;
      let totalCosto = 0;
      let dias = 0;

      monthDays.forEach((dayInfo, dayIndex) => {
        const key = cellKey(service._id, dayInfo.key);
        const record = recordIndex[key];
        const hasDraft = Object.prototype.hasOwnProperty.call(drafts, key);
        const storedValue = record && record.contadorNuevo !== undefined && record.contadorNuevo !== null
          ? String(record.contadorNuevo)
          : '';
        const value = hasDraft ? drafts[key] : storedValue;

        const cell = { value, record };
        const parsed = parseReading(value);

        if (parsed === null) {
          // Celda vacía. Si había un registro y el usuario la borró, se
          // interpreta como eliminación pendiente y así se avisa.
          if (record && hasDraft) {
            cell.error = 'Vacía: usa el botón eliminar';
            invalid.push({ service, dayInfo });
          }
          cells[key] = cell;
          return;
        }

        if (Number.isNaN(parsed)) {
          cell.error = 'Valor inválido';
          cells[key] = cell;
          invalid.push({ service, dayInfo });
          return;
        }

        if (parsed < 0) {
          cell.error = 'No puede ser negativo';
          cells[key] = cell;
          invalid.push({ service, dayInfo });
          return;
        }

        if (previous === null) {
          // Sin lectura previa no hay consumo que calcular. Se avisa en vez de
          // inventar una base, porque guardaría un consumo de cero.
          cell.error = 'Falta la lectura base';
          cells[key] = cell;
          invalid.push({ service, dayInfo });
          return;
        }

        if (parsed < previous) {
          cell.error = `Debe ser ≥ ${formatNumber(previous)}`;
          cells[key] = cell;
          invalid.push({ service, dayInfo });
          return;
        }

        const contadorAnterior = previous;
        const consumo = computeConsumo(contadorAnterior, parsed);
        const chain = {
          contadorAnterior,
          contadorNuevo: parsed,
          consumo,
          consumoLitros: computeLitros(consumo, service),
          // Tarifa del propio registro si ya existe; la del servicio sólo para
          // lecturas nuevas (ver resolveUnitPrice).
          costo: computeCosto(consumo, resolveUnitPrice(record, service)),
        };

        cell.chain = chain;
        cell.dirty = value !== storedValue;
        cell.overLimit = Number(service.consumoLimiteDia) > 0
          && consumo > Number(service.consumoLimiteDia);

        if (cell.dirty) firstEditedIndex = Math.min(firstEditedIndex, dayIndex);
        if (cell.overLimit) overLimit.push({ service, dayInfo, consumo });

        cells[key] = cell;
        serviceRows.push({ dayIndex, dayInfo, record, chain, isDirty: cell.dirty });

        totalConsumo += consumo;
        totalCosto += chain.costo;
        dias += 1;
        previous = parsed;
      });

      // Clasificación de lo que hay que persistir.
      serviceRows.forEach(({ dayIndex, dayInfo, record, chain, isDirty }) => {
        const entry = { service, dayInfo, record, chain };

        // El desfase se mide sobre la cadena de lecturas (contador anterior y
        // consumo). El costo queda fuera a propósito: depende de la tarifa, y
        // una tarifa que cambió no significa que el encadenamiento esté mal.
        const derivedChanged = record && !(
          isSameNumber(record.contadorAnterior, chain.contadorAnterior)
          && isSameNumber(record.contadorNuevo, chain.contadorNuevo)
          && isSameNumber(record.consumo, chain.consumo)
        );

        if (isDirty || !record) {
          directEdits.push(entry);
        } else if (derivedChanged && dayIndex > firstEditedIndex) {
          // El arrastre sólo alcanza a los días posteriores al primer día
          // editado por el usuario: es la consecuencia directa de su cambio.
          cascade.push(entry);
        }

        // Desfase histórico: registros cuyo consumo guardado no coincide con
        // la cadena, sin que el usuario haya tocado nada. Se recalculan sólo
        // si lo pide explícitamente.
        if (derivedChanged && !isDirty) drift.push(entry);
      });

      totals[service._id] = { consumo: totalConsumo, costo: totalCosto, dias };
    });

    return { cells, totals, directEdits, cascade, drift, invalid, overLimit };
  }, [services, monthDays, recordIndex, drafts, baselines]);

  const pendingCount = model.directEdits.length + model.cascade.length;
  const hasUnsavedWork = pendingCount > 0;
  const hasInvalidCells = model.invalid.length > 0;

  const monthTotalCost = useMemo(
    () => Object.values(model.totals).reduce((acc, total) => acc + (total.costo || 0), 0),
    [model.totals]
  );

  // ---------- EDICIÓN ----------

  const handleCellChange = useCallback((serviceId, dayKey, value) => {
    setDrafts((prev) => ({ ...prev, [cellKey(serviceId, dayKey)]: value }));
  }, []);

  const handleBaselineChange = useCallback((serviceId, value) => {
    setBaselines((prev) => ({
      ...prev,
      [serviceId]: { ...(prev[serviceId] || {}), value, source: 'manual', label: 'base manual' },
    }));
  }, []);

  const handleDiscard = useCallback(() => {
    setDrafts({});
  }, []);

  const toggleService = useCallback((serviceId) => {
    setHiddenServices((prev) => ({ ...prev, [serviceId]: !prev[serviceId] }));
  }, []);

  // ---------- NAVEGACIÓN DE PERIODO ----------

  const applyPeriod = useCallback((year, month) => {
    setDrafts({});
    setPeriod({ year, month });
  }, []);

  const requestPeriodChange = useCallback((year, month) => {
    if (hasUnsavedWork) {
      setPendingNavigation({ year, month });
      return;
    }
    applyPeriod(year, month);
  }, [hasUnsavedWork, applyPeriod]);

  /**
   * Avanza o retrocede meses con las flechas.
   *
   * El desplazamiento se calcula dentro del actualizador de estado, no sobre
   * el `period` capturado en el render: así varios clics seguidos se acumulan
   * (tres clics = tres meses) en vez de pisarse entre ellos.
   */
  const shiftMonth = useCallback((offset) => {
    if (hasUnsavedWork) {
      const target = new Date(period.year, period.month + offset, 1);
      setPendingNavigation({ year: target.getFullYear(), month: target.getMonth() });
      return;
    }
    setDrafts({});
    setPeriod((current) => {
      const target = new Date(current.year, current.month + offset, 1);
      return { year: target.getFullYear(), month: target.getMonth() };
    });
  }, [hasUnsavedWork, period]);

  const goToCurrentMonth = useCallback(() => {
    const today = new Date();
    requestPeriodChange(today.getFullYear(), today.getMonth());
  }, [requestPeriodChange]);

  // ---------- PERSISTENCIA ----------

  /** Deja el contador del servicio igual a su lectura más reciente. */
  const syncServiceCounter = useCallback(async (serviceId) => {
    try {
      const response = await fetchUtilityServices({
        limit: 1,
        page: 1,
        query: { 'lavanderia.id': idLavanderia, 'servicio.id': serviceId },
      });
      const latest = Array.isArray(response?.data) ? response.data[0] : null;
      if (latest && Number.isFinite(Number(latest.contadorNuevo))) {
        await updateServiceCounter(serviceId, Number(latest.contadorNuevo));
      }
    } catch (error) {
      // El contador de parametrización es informativo: un fallo aquí no
      // invalida los registros ya guardados.
      console.error('No se pudo sincronizar el contador del servicio:', error);
    }
  }, [idLavanderia]);

  /** Persiste una lista de entradas del plan, en orden cronológico. */
  const persistEntries = useCallback(async (entries) => {
    const ordered = [...entries].sort((a, b) => a.dayInfo.day - b.dayInfo.day);
    const touchedServices = new Set();
    let created = 0;
    let updated = 0;
    const failures = [];

    for (const entry of ordered) {
      const { service, dayInfo, record, chain } = entry;
      const payload = {
        contadorAnterior: chain.contadorAnterior,
        contadorNuevo: chain.contadorNuevo,
        consumo: chain.consumo,
        consumoLitros: chain.consumoLitros,
        costo: chain.costo,
      };

      try {
        if (record) {
          await updateUtilityService(record._id, {
            ...payload,
            fecha: buildUpdateDateIso(period.year, period.month, dayInfo.day),
          });
          updated += 1;
        } else {
          await createUtilityService({
            ...payload,
            // El endpoint de creación resta un día: se envía día + 1.
            fecha: buildCreateDateIso(period.year, period.month, dayInfo.day),
            servicio: {
              id: service._id,
              nombre: service.nombre,
              tipo: service.tipo,
              unidad: service.unidad,
              precio: service.precio,
            },
            lavanderia: { id: idLavanderia, nombre: laundryName },
          });
          created += 1;
        }
        touchedServices.add(service._id);
      } catch (error) {
        console.error('Error al guardar la lectura:', error);
        failures.push(`${service.nombre} · día ${dayInfo.day}`);
      }
    }

    for (const serviceId of touchedServices) {
      await syncServiceCounter(serviceId);
    }

    return { created, updated, failures };
  }, [period.year, period.month, idLavanderia, laundryName, syncServiceCounter]);

  const runSave = useCallback(async (entries, { label = 'Cambios guardados' } = {}) => {
    setIsSaving(true);
    try {
      const { created, updated, failures } = await persistEntries(entries);

      setDrafts({});
      await loadAll({ quiet: true });

      const parts = [];
      if (created) parts.push(`${created} nuevo${created === 1 ? '' : 's'}`);
      if (updated) parts.push(`${updated} actualizado${updated === 1 ? '' : 's'}`);

      if (failures.length > 0) {
        toast({
          variant: 'destructive',
          title: 'Guardado parcial',
          description: `${parts.join(' · ') || 'Sin cambios'}. Falló: ${failures.slice(0, 4).join(', ')}${failures.length > 4 ? '…' : ''}`,
        });
      } else {
        toast({ title: label, description: parts.join(' · ') || 'Sin cambios por aplicar' });
      }
    } catch (error) {
      console.error('Error al guardar la planilla:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.message || 'No se pudieron guardar los cambios.',
      });
    } finally {
      setIsSaving(false);
    }
  }, [persistEntries, loadAll, toast]);

  const handleSaveClick = useCallback(() => {
    if (model.invalid.length > 0) {
      toast({
        variant: 'destructive',
        title: 'Hay lecturas inválidas',
        description: `Corrige ${model.invalid.length} celda${model.invalid.length === 1 ? '' : 's'} marcada${model.invalid.length === 1 ? '' : 's'} en rojo antes de guardar.`,
      });
      return;
    }

    if (pendingCount === 0) return;

    const entries = [...model.directEdits, ...model.cascade];
    const affectedOverLimit = model.overLimit.filter(({ service, dayInfo }) => {
      const cell = model.cells[cellKey(service._id, dayInfo.key)];
      return cell?.dirty;
    });

    // Sólo se pide confirmación cuando hay algo que el operario debería
    // revisar: consumos por encima del límite diario o arrastre de la cadena.
    if (affectedOverLimit.length > 0 || model.cascade.length > 0) {
      setPendingSave({ entries, overLimit: affectedOverLimit, cascade: model.cascade.length });
      return;
    }

    runSave(entries);
  }, [model, pendingCount, runSave, toast]);

  const handleDeleteRecord = useCallback((serviceId, dayKey) => {
    const cell = model.cells[cellKey(serviceId, dayKey)];
    if (!cell?.record) return;
    const service = services.find((item) => item._id === serviceId);
    const dayInfo = monthDays.find((item) => item.key === dayKey);
    setPendingDeletion({ record: cell.record, service, dayInfo });
  }, [model.cells, services, monthDays]);

  const confirmDeletion = useCallback(async () => {
    if (!pendingDeletion) return;
    const { record, service } = pendingDeletion;
    setPendingDeletion(null);
    setIsSaving(true);
    try {
      await deleteUtilityService(record._id);
      if (service?._id) await syncServiceCounter(service._id);
      await loadAll({ quiet: true });
      toast({ title: 'Registro eliminado' });
    } catch (error) {
      console.error('Error al eliminar el registro:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.message || 'No se pudo eliminar el registro.',
      });
    } finally {
      setIsSaving(false);
    }
  }, [pendingDeletion, loadAll, syncServiceCounter, toast]);

  // ---------- RENDER ----------

  const yearOptions = useMemo(() => {
    const current = new Date().getFullYear();
    return Array.from({ length: 7 }, (_, index) => current - 5 + index);
  }, []);

  const isCurrentMonth = period.year === now.getFullYear() && period.month === now.getMonth();

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2 }}
      className="p-4 md:p-6 lg:p-8"
    >
      <div className="mb-4">
        <Button asChild variant="outline">
          <Link to={`/centros-lavado/${idLavanderia}`}>
            <ArrowLeft className="mr-2 h-4 w-4" /> Volver al Centro de Lavado
          </Link>
        </Button>
      </div>

      {/* ---------- UBICACIÓN Y CONTEXTO ---------- */}
      <div className="mb-5 rounded-lg border border-border bg-card p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold tracking-tight">
              Planilla de lecturas
              {laundryName ? <span className="text-muted-foreground"> · {laundryName}</span> : null}
            </h1>
            <p className="text-sm text-muted-foreground">
              Un renglón por día, una columna por medidor. Escribe la lectura y guarda todo el mes de una vez.
            </p>
          </div>
          <Button asChild variant="ghost" size="sm">
            <Link to={`/centros-lavado/${idLavanderia}/servicios`}>
              <LayoutList className="mr-2 h-4 w-4" /> Vista clásica
            </Link>
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="space-y-4 pb-4">
          {/* ---------- PERIODO Y ACCIONES ---------- */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                onClick={() => shiftMonth(-1)}
                disabled={isSaving}
                aria-label="Mes anterior"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>

              <Select
                value={String(period.month)}
                onValueChange={(value) => requestPeriodChange(period.year, Number(value))}
              >
                <SelectTrigger className="h-10 w-[140px]" aria-label="Mes">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MONTH_NAMES.map((name, index) => (
                    <SelectItem key={name} value={String(index)}>{name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={String(period.year)}
                onValueChange={(value) => requestPeriodChange(Number(value), period.month)}
              >
                <SelectTrigger className="h-10 w-[100px]" aria-label="Año">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {yearOptions.map((year) => (
                    <SelectItem key={year} value={String(year)}>{year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button
                variant="outline"
                size="icon"
                onClick={() => shiftMonth(1)}
                disabled={isSaving}
                aria-label="Mes siguiente"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>

              {!isCurrentMonth && (
                <Button variant="ghost" size="sm" onClick={goToCurrentMonth} disabled={isSaving}>
                  Hoy
                </Button>
              )}
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                onClick={() => loadAll()}
                disabled={isLoading || isSaving}
                aria-label="Recargar"
                title="Recargar la planilla"
              >
                <RefreshCw className={cn('h-4 w-4', isLoading && 'animate-spin')} />
              </Button>

              {hasUnsavedWork && (
                <Button variant="ghost" size="sm" onClick={handleDiscard} disabled={isSaving}>
                  <RotateCcw className="mr-2 h-4 w-4" /> Descartar
                </Button>
              )}

              <Button
                onClick={handleSaveClick}
                disabled={isSaving || !hasUnsavedWork || hasInvalidCells}
                title={
                  hasInvalidCells
                    ? 'Corrige las lecturas marcadas en rojo para poder guardar'
                    : undefined
                }
              >
                {isSaving
                  ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  : <Save className="mr-2 h-4 w-4" />}
                {hasUnsavedWork && !hasInvalidCells ? `Guardar ${pendingCount}` : 'Guardar'}
              </Button>
            </div>
          </div>

          {/* ---------- MEDIDORES VISIBLES ---------- */}
          {services.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="mr-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Medidores
              </span>
              {services.map((service) => {
                const Icon = SERVICE_ICONS[service.tipo] || HelpCircle;
                const color = SERVICE_COLORS[service.tipo];
                const isHidden = Boolean(hiddenServices[service._id]);
                return (
                  <button
                    key={service._id}
                    type="button"
                    onClick={() => toggleService(service._id)}
                    aria-pressed={!isHidden}
                    className={cn(
                      'flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition-colors',
                      'focus:outline-none focus:ring-1 focus:ring-ring',
                      isHidden
                        ? 'border-border/60 text-muted-foreground/60 hover:bg-accent/50'
                        : 'border-border bg-accent/60 font-medium text-foreground hover:bg-accent'
                    )}
                  >
                    <Icon
                      className={cn('h-3 w-3', isHidden && 'opacity-40')}
                      color={isHidden ? undefined : color}
                    />
                    {service.nombre}
                  </button>
                );
              })}
            </div>
          )}

          {/* ---------- AVISOS ---------- */}
          {model.drift.length > 0 && model.directEdits.length === 0 && canEditExisting && (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-amber-500/40 bg-amber-500/[0.07] px-3 py-2">
              <div className="flex items-start gap-2 text-xs">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600 dark:text-amber-400" />
                <span className="text-muted-foreground">
                  <span className="font-medium text-foreground">
                    {model.drift.length} registro{model.drift.length === 1 ? '' : 's'}
                  </span>{' '}
                  guarda{model.drift.length === 1 ? '' : 'n'} un consumo que no coincide con la cadena
                  de lecturas de este mes.
                </span>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPendingRecalc(true)}
                disabled={isSaving}
              >
                Recalcular mes
              </Button>
            </div>
          )}

          {model.invalid.length > 0 && (
            <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/[0.07] px-3 py-2 text-xs">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-destructive" />
              <span className="text-muted-foreground">
                <span className="font-medium text-foreground">
                  {model.invalid.length} celda{model.invalid.length === 1 ? '' : 's'}
                </span>{' '}
                con lecturas inválidas. Recuerda que el contador es acumulativo: cada lectura debe ser
                mayor o igual a la anterior.
              </span>
            </div>
          )}
        </CardHeader>

        <CardContent className="space-y-3">
          {isLoading ? (
            <div className="flex items-center justify-center gap-2 py-16 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span>Cargando la planilla de {MONTH_NAMES[period.month]} {period.year}…</span>
            </div>
          ) : services.length === 0 ? (
            <div className="rounded-md border border-dashed border-border p-12 text-center">
              <p className="mb-1 text-sm">Esta lavandería no tiene servicios públicos configurados.</p>
              <p className="text-xs text-muted-foreground">
                Regístralos primero en Parametrización para poder tomar lecturas.
              </p>
            </div>
          ) : (
            <>
              <MonthlyServicesGrid
                monthDays={monthDays}
                services={visibleServices}
                cells={model.cells}
                baselines={baselines}
                totals={model.totals}
                canEditExisting={canEditExisting}
                isSaving={isSaving}
                onCellChange={handleCellChange}
                onBaselineChange={handleBaselineChange}
                onDeleteRecord={handleDeleteRecord}
              />

              {/* ---------- LEYENDA Y COSTO DEL MES ---------- */}
              <div className="flex flex-wrap items-center justify-between gap-3 text-[11px] text-muted-foreground">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                  <span className="flex items-center gap-1.5">
                    <span className="h-3 w-[2px] bg-primary" /> sin guardar
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="h-3 w-3 rounded-sm border border-amber-500/40 bg-amber-500/[0.15]" />
                    supera el límite diario
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="h-3 w-3 rounded-sm border border-destructive/50 bg-destructive/[0.12]" />
                    lectura inválida
                  </span>
                  <span>Enter o ↑↓ para moverte por la columna</span>
                </div>
                <div>
                  Costo del mes{' '}
                  <span className="font-semibold tabular-nums text-foreground">
                    {formatCurrency(monthTotalCost)}
                  </span>
                </div>
              </div>

              {!canEditExisting && (
                <p className="text-[11px] text-muted-foreground">
                  Puedes registrar lecturas nuevas. Modificar o eliminar un registro ya guardado
                  requiere permisos de administrador.
                </p>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* ---------- CONFIRMAR GUARDADO CON ADVERTENCIAS ---------- */}
      <AlertDialog open={Boolean(pendingSave)} onOpenChange={(open) => !open && setPendingSave(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Revisa antes de guardar
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm">
                {pendingSave?.overLimit?.length > 0 && (
                  <div>
                    <p className="mb-1">
                      {pendingSave.overLimit.length} lectura{pendingSave.overLimit.length === 1 ? '' : 's'}{' '}
                      supera{pendingSave.overLimit.length === 1 ? '' : 'n'} el límite de consumo diario
                      configurado:
                    </p>
                    <ul className="max-h-32 space-y-0.5 overflow-auto rounded-md bg-muted p-2 text-xs">
                      {pendingSave.overLimit.slice(0, 8).map(({ service, dayInfo, consumo }) => (
                        <li key={`${service._id}-${dayInfo.key}`} className="tabular-nums">
                          Día {dayInfo.day} · {service.nombre}: {formatNumber(consumo)} {service.unidad}
                          <span className="text-muted-foreground">
                            {' '}(límite {formatNumber(service.consumoLimiteDia)})
                          </span>
                        </li>
                      ))}
                      {pendingSave.overLimit.length > 8 && (
                        <li className="text-muted-foreground">
                          y {pendingSave.overLimit.length - 8} más…
                        </li>
                      )}
                    </ul>
                  </div>
                )}
                {pendingSave?.cascade > 0 && (
                  <p>
                    Además se recalcularán{' '}
                    <span className="font-medium text-foreground">{pendingSave.cascade} registro{pendingSave.cascade === 1 ? '' : 's'}</span>{' '}
                    posterior{pendingSave.cascade === 1 ? '' : 'es'} del mes, porque su contador anterior
                    cambia al encadenarse con tu edición.
                  </p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Revisar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const entries = pendingSave?.entries || [];
                setPendingSave(null);
                runSave(entries);
              }}
            >
              Guardar de todos modos
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ---------- CONFIRMAR RECÁLCULO DEL MES ---------- */}
      <AlertDialog open={pendingRecalc} onOpenChange={setPendingRecalc}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Recalcular el mes</AlertDialogTitle>
            <AlertDialogDescription>
              Se reescribirán el contador anterior, el consumo y el costo de {model.drift.length}{' '}
              registro{model.drift.length === 1 ? '' : 's'} de {MONTH_NAMES[period.month]} {period.year},
              tomando cada lectura como referencia de la siguiente. Las lecturas de los contadores no
              se modifican, y cada registro conserva la tarifa con la que se calculó: el costo se
              ajusta sólo en proporción al consumo corregido.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const entries = model.drift;
                setPendingRecalc(false);
                runSave(entries, { label: 'Mes recalculado' });
              }}
            >
              Recalcular
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ---------- CONFIRMAR ELIMINACIÓN ---------- */}
      <AlertDialog
        open={Boolean(pendingDeletion)}
        onOpenChange={(open) => !open && setPendingDeletion(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Eliminar la lectura
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p>Se eliminará este registro de consumo:</p>
                <div className="rounded-md bg-muted p-3 text-xs">
                  <p><span className="font-medium">Servicio:</span> {pendingDeletion?.service?.nombre}</p>
                  <p>
                    <span className="font-medium">Fecha:</span> {pendingDeletion?.dayInfo?.day}{' '}
                    de {MONTH_NAMES[period.month]} {period.year}
                  </p>
                  <p className="tabular-nums">
                    <span className="font-medium">Lectura:</span>{' '}
                    {formatNumber(pendingDeletion?.record?.contadorNuevo)}{' '}
                    · consumo {formatNumber(pendingDeletion?.record?.consumo)}{' '}
                    {pendingDeletion?.service?.unidad}
                  </p>
                </div>
                <p className="text-destructive">Esta acción no se puede deshacer.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeletion}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ---------- CAMBIO DE MES CON CAMBIOS SIN GUARDAR ---------- */}
      <AlertDialog
        open={Boolean(pendingNavigation)}
        onOpenChange={(open) => !open && setPendingNavigation(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Tienes cambios sin guardar</AlertDialogTitle>
            <AlertDialogDescription>
              Hay {pendingCount} cambio{pendingCount === 1 ? '' : 's'} pendiente
              {pendingCount === 1 ? '' : 's'} en {MONTH_NAMES[period.month]} {period.year}. Si cambias
              de mes ahora se descartarán.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Seguir editando</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pendingNavigation) applyPeriod(pendingNavigation.year, pendingNavigation.month);
                setPendingNavigation(null);
              }}
            >
              Descartar y cambiar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </motion.div>
  );
};

export default UtilityServicesGridPage;
